import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { adminUserService } from '../../services/adminUserService';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: ''
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const data = await adminUserService?.getAllAdmins();
      setAdmins(data);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar administradores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({ username: '', password: '', fullName: '' });
    setError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ username: '', password: '', fullName: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setError('');
      await adminUserService?.createAdmin(
        formData?.username,
        formData?.password,
        formData?.fullName
      );
      handleCloseModal();
      await loadAdmins();
    } catch (err) {
      // Display user-friendly error message
      const errorMessage = err?.message || 'Erro ao criar administrador';
      setError(errorMessage);
      console.error('Error creating admin user:', err);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await adminUserService?.updateAdminStatus(userId, !currentStatus);
      loadAdmins();
    } catch (err) {
      setError(err?.message || 'Erro ao atualizar status');
    }
  };

  return (
    <>
      <Helmet>
        <title>Gerenciar Administradores - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie usuários administradores do sistema" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <AdminNavigation />
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Gerenciar Administradores
              </h1>
              <p className="text-muted-foreground">
                Adicione e gerencie usuários com permissões administrativas
              </p>
            </div>
            <Button
              onClick={handleOpenModal}
              iconName="UserPlus"
              className="glow-primary"
            >
              Novo Admin
            </Button>
          </div>

          {error && !isModalOpen && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin glow-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {admins?.map((admin) => (
                <div
                  key={admin?.id}
                  className="bg-card rounded-lg p-6 glow-primary hover:glow-primary-hover transition-smooth"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center glow-primary">
                        <Icon name="User" size={24} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-bold text-foreground">
                          {admin?.fullName}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          @{adminUserService?.getUsername(admin?.email)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      admin?.isActive 
                        ? 'bg-green-500/10 text-green-500' :'bg-red-500/10 text-red-500'
                    }`}>
                      {admin?.isActive ? 'Ativo' : 'Inativo'}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(admin?.id, admin?.isActive)}
                    >
                      {admin?.isActive ? 'Desativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card rounded-lg p-6 w-full max-w-md glow-primary">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Novo Administrador
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="p-2 rounded-md hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500 rounded-lg text-sm text-red-500">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome Completo
                  </label>
                  <Input
                    value={formData?.fullName}
                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e?.target?.value }))}
                    placeholder="Nome do administrador"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Usuário
                  </label>
                  <Input
                    value={formData?.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e?.target?.value }))}
                    placeholder="usuario"
                    required
                    pattern="[a-zA-Z0-9_]+"
                    title="Apenas letras, números e underscore"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Apenas letras, números e underscore (_)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Senha
                  </label>
                  <Input
                    type="password"
                    value={formData?.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e?.target?.value }))}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCloseModal}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="glow-primary">
                    Criar Admin
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminUsers;